import type { ButtonBaseActions } from '@mui/material/ButtonBase';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@shared/ui';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLazyGetAppointmentsQuery } from '../../appointments/api/appointmentApi';
import type { BrowserAppointment } from '../../appointments/types';
import { useGetCalendarAvailabilityQuery } from '../availability/availabilityApi';
import TimeSelect from '../TimeSelect';
import {
  useCreateOutOfOfficeMutation,
  useDeleteOutOfOfficeMutation,
  useGetOutOfOfficeQuery,
} from './outOfOfficeApi';
import type { CreateOutOfOfficeRequest, OutOfOfficeBlock } from './outOfOfficeTypes';

type ConflictWarning = 'overlap' | 'check-failed' | null;

interface OutOfOfficeDraft {
  allDay: boolean;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

const DEFAULT_START_TIME = '09:00';
const DEFAULT_END_TIME = '17:00';
const EMPTY_DRAFT: OutOfOfficeDraft = {
  allDay: false,
  startDate: '',
  startTime: DEFAULT_START_TIME,
  endDate: '',
  endTime: DEFAULT_END_TIME,
};
const MAX_DISPLAYED_CONFLICTS = 10;

const OutOfOfficeCard: React.FC = () => {
  const { data: availability } = useGetCalendarAvailabilityQuery();
  const { data: blocks, error: listError, isLoading, isFetching } = useGetOutOfOfficeQuery();
  const [createOutOfOffice, createState] = useCreateOutOfOfficeMutation();
  const [deleteOutOfOffice, deleteState] = useDeleteOutOfOfficeMutation();
  const [getAppointments] = useLazyGetAppointmentsQuery();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<OutOfOfficeDraft>(EMPTY_DRAFT);
  const [validationError, setValidationError] = useState('');
  const [conflictWarning, setConflictWarning] = useState<ConflictWarning>(null);
  const [conflictingAppointments, setConflictingAppointments] = useState<BrowserAppointment[]>([]);
  const [hasAdditionalConflicts, setHasAdditionalConflicts] = useState(false);
  const [savedBlockAlreadyEnded, setSavedBlockAlreadyEnded] = useState(false);
  const [blockPendingDeletion, setBlockPendingDeletion] = useState<OutOfOfficeBlock | null>(null);
  const [visibleAt, setVisibleAt] = useState(Date.now);
  const addOutOfOfficeButtonActionRef = useRef<ButtonBaseActions>(null);
  const focusAddButtonAfterDeleteRef = useRef(false);
  const submittingRef = useRef(false);
  const conflictCheckSequenceRef = useRef(0);
  const conflictBlockIDRef = useRef<string | null>(null);
  const availabilityTimeZone = availability?.time_zone.trim();
  const requiresTimeZoneSelection = availabilityTimeZone === 'Local';
  const canCreateOutOfOffice = Boolean(availabilityTimeZone && !requiresTimeZoneSelection);

  useEffect(() => {
    const now = Date.now();
    const blockEndTimes = blocks?.map((block) => new Date(block.end_at).getTime()) ?? [];
    const nextEnd = blockEndTimes
      .filter((endAt) => endAt > now)
      .sort((left, right) => left - right)[0];
    if (!nextEnd) {
      if (blockEndTimes.some((endAt) => endAt > visibleAt && endAt <= now)) {
        setVisibleAt(now);
      }
      return;
    }
    const timer = window.setTimeout(
      () => setVisibleAt(Date.now()),
      Math.min(nextEnd - now + 1, 2_147_483_647)
    );
    return () => window.clearTimeout(timer);
  }, [blocks, visibleAt]);

  const closeAndResetDialog = () => {
    setDialogOpen(false);
    setDraft(EMPTY_DRAFT);
    setValidationError('');
  };

  const handleSubmit = async () => {
    if (submittingRef.current) {
      return;
    }
    const validationMessage = validateDraft(draft);
    if (validationMessage || !canCreateOutOfOffice || !availabilityTimeZone) {
      setValidationError(
        validationMessage ||
          (requiresTimeZoneSelection
            ? 'Choose a timezone in Availability before adding out-of-office time.'
            : 'Availability timezone is still loading.')
      );
      return;
    }

    submittingRef.current = true;
    setValidationError('');
    setSavedBlockAlreadyEnded(false);
    let savedBlock: OutOfOfficeBlock;
    try {
      savedBlock = await createOutOfOffice(toCreateRequest(draft, availabilityTimeZone)).unwrap();
      closeAndResetDialog();
    } catch {
      // RTK Query mutation state renders the save error while preserving the draft.
      return;
    } finally {
      submittingRef.current = false;
    }

    const conflictCheckSequence = ++conflictCheckSequenceRef.current;
    conflictBlockIDRef.current = savedBlock.id;
    setConflictWarning(null);
    setConflictingAppointments([]);
    setHasAdditionalConflicts(false);

    // An already-ended block is persisted as history but filtered out of the active
    // list, so say so rather than leaving the coach staring at an unchanged page.
    if (new Date(savedBlock.end_at).getTime() <= Date.now()) {
      setSavedBlockAlreadyEnded(true);
      return;
    }

    try {
      const page = await getAppointments({
        booking_status: 'confirmed',
        lifecycle_status: 'scheduled',
        range_start: savedBlock.start_at,
        range_end: savedBlock.end_at,
        participant_role: 'coach',
        limit: 100,
        cursor: null,
      }).unwrap();
      if (conflictCheckSequenceRef.current === conflictCheckSequence) {
        setConflictingAppointments(page.appointments.slice(0, MAX_DISPLAYED_CONFLICTS));
        setHasAdditionalConflicts(
          page.next_cursor !== null || page.appointments.length > MAX_DISPLAYED_CONFLICTS
        );
        setConflictWarning(page.appointments.length > 0 ? 'overlap' : null);
      }
    } catch {
      if (conflictCheckSequenceRef.current === conflictCheckSequence) {
        setConflictWarning('check-failed');
      }
    }
  };

  const handleDelete = async () => {
    if (!blockPendingDeletion) {
      return;
    }
    const id = blockPendingDeletion.id;
    try {
      await deleteOutOfOffice({ id }).unwrap();
      if (conflictBlockIDRef.current === id) {
        ++conflictCheckSequenceRef.current;
        conflictBlockIDRef.current = null;
        setConflictWarning(null);
        setConflictingAppointments([]);
        setHasAdditionalConflicts(false);
      }
      focusAddButtonAfterDeleteRef.current = true;
      setBlockPendingDeletion(null);
    } catch {
      // Keep the confirmation open so the same stable-ID deletion can be retried.
    }
  };

  const updateBlockPendingDeletion = (block: OutOfOfficeBlock | null) => {
    focusAddButtonAfterDeleteRef.current = false;
    deleteState.reset();
    setBlockPendingDeletion(block);
  };

  const handleDeleteDialogExited = () => {
    if (!focusAddButtonAfterDeleteRef.current) {
      return;
    }
    focusAddButtonAfterDeleteRef.current = false;
    addOutOfOfficeButtonActionRef.current?.focusVisible();
  };

  const loading = isLoading || (!blocks && isFetching);
  const visibleBlocks = blocks?.filter((block) => new Date(block.end_at).getTime() > visibleAt);

  return (
    <Card
      data-testid="out-of-office-card"
      variant="outlined"
      sx={{ bgcolor: 'transparent', borderColor: '#E3E3E3', borderRadius: 2 }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 4 }, '&:last-child': { pb: { xs: 2.5, md: 4 } } }}>
        <Box
          sx={{ alignItems: 'center', display: 'flex', gap: 2, justifyContent: 'space-between' }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ color: '#262626', fontSize: 16, fontWeight: 700, lineHeight: 1.5 }}
            >
              Out of Office
            </Typography>
            <Typography sx={{ color: '#A4A4A4', fontSize: 12, lineHeight: 1.5 }}>
              Block time when members should not book sessions.
            </Typography>
            {requiresTimeZoneSelection ? (
              <Typography sx={{ color: '#B42318', fontSize: 12, lineHeight: 1.5 }}>
                Choose a timezone in Availability before adding out-of-office time.
              </Typography>
            ) : null}
          </Box>
          <Button
            action={addOutOfOfficeButtonActionRef}
            type="button"
            variant="outlined"
            size="small"
            disabled={!canCreateOutOfOffice}
            onClick={() => {
              setSavedBlockAlreadyEnded(false);
              setDialogOpen(true);
            }}
          >
            Add out of office
          </Button>
        </Box>

        <Divider sx={{ borderColor: '#E3E3E3', my: 2.5 }} />

        <OutOfOfficeMessages
          loading={loading}
          listError={Boolean(listError)}
          deleteError={deleteState.isError}
          conflictWarning={conflictWarning}
          savedBlockAlreadyEnded={savedBlockAlreadyEnded}
          conflictingAppointments={conflictingAppointments}
          hasAdditionalConflicts={hasAdditionalConflicts}
        />

        {visibleBlocks?.length ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {visibleBlocks.map((block) => (
              <Box
                key={block.id}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 2,
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography sx={{ color: '#262626', fontSize: 14 }}>
                    {formatBlockRange(block)}
                  </Typography>
                  <Typography sx={{ color: '#777', fontSize: 12 }}>{block.time_zone}</Typography>
                </Box>
                <Button
                  type="button"
                  size="small"
                  color="error"
                  aria-label="Delete out of office"
                  disabled={deleteState.isLoading}
                  onClick={() => updateBlockPendingDeletion(block)}
                >
                  Delete
                </Button>
              </Box>
            ))}
          </Box>
        ) : null}

        {!loading && !listError && visibleBlocks?.length === 0 ? (
          <Typography sx={{ color: '#777', fontSize: 13 }}>
            No active out-of-office blocks.
          </Typography>
        ) : null}

        <OutOfOfficeDialog
          open={dialogOpen}
          draft={draft}
          timeZone={availability?.time_zone}
          validationError={validationError}
          createError={createState.isError}
          saving={createState.isLoading}
          onChange={setDraft}
          onClose={closeAndResetDialog}
          onSubmit={() => void handleSubmit()}
        />
        <Dialog
          open={blockPendingDeletion !== null}
          onClose={deleteState.isLoading ? undefined : () => updateBlockPendingDeletion(null)}
          TransitionProps={{ onExited: handleDeleteDialogExited }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Delete out of office?</DialogTitle>
          <DialogContent>
            <Typography>
              Deleting this out-of-office block will reopen this time for member bookings. Existing
              appointments will not change.
            </Typography>
            {deleteState.isError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                We couldn’t delete out of office. Please try again.
              </Alert>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              type="button"
              variant="text"
              disabled={deleteState.isLoading}
              onClick={() => updateBlockPendingDeletion(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              color="error"
              disabled={deleteState.isLoading}
              onClick={() => void handleDelete()}
            >
              Delete out of office
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

interface OutOfOfficeMessagesProps {
  loading: boolean;
  listError: boolean;
  deleteError: boolean;
  conflictWarning: ConflictWarning;
  conflictingAppointments: BrowserAppointment[];
  hasAdditionalConflicts: boolean;
  savedBlockAlreadyEnded: boolean;
}

const OutOfOfficeMessages: React.FC<OutOfOfficeMessagesProps> = ({
  loading,
  listError,
  deleteError,
  conflictWarning,
  conflictingAppointments,
  hasAdditionalConflicts,
  savedBlockAlreadyEnded,
}) => (
  <>
    {loading ? (
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 2 }}>
        <CircularProgress size={18} />
        <Typography sx={{ fontSize: 13 }}>Loading out-of-office blocks…</Typography>
      </Box>
    ) : null}
    {listError ? <Alert severity="error">We couldn’t load out-of-office blocks.</Alert> : null}
    {deleteError ? (
      <Alert severity="error">We couldn’t delete out of office. Please try again.</Alert>
    ) : null}
    {conflictWarning === 'overlap' ? (
      <Alert severity="warning">
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          Block saved. Reach out to these members to reschedule:
        </Typography>
        {conflictingAppointments.map((appointment) => (
          <Typography key={appointment.id} sx={{ fontSize: 13 }}>
            {appointment.expected_identities.member.display_name} —{' '}
            {formatAppointmentStart(appointment)}
          </Typography>
        ))}
        {hasAdditionalConflicts ? (
          <Typography sx={{ fontSize: 13 }}>
            More scheduled sessions may overlap this block.
          </Typography>
        ) : null}
      </Alert>
    ) : null}
    {conflictWarning === 'check-failed' ? (
      <Alert severity="warning">Block saved, but conflicts could not be checked.</Alert>
    ) : null}
    {savedBlockAlreadyEnded ? (
      <Alert severity="warning">
        Block saved, but it already ended in your availability timezone, so it is not listed as
        active and does not block any bookings.
      </Alert>
    ) : null}
  </>
);

interface OutOfOfficeDialogProps {
  open: boolean;
  draft: OutOfOfficeDraft;
  timeZone?: string;
  validationError: string;
  createError: boolean;
  saving: boolean;
  onChange: (draft: OutOfOfficeDraft) => void;
  onClose: () => void;
  onSubmit: () => void;
}

const OutOfOfficeDialog: React.FC<OutOfOfficeDialogProps> = ({
  open,
  draft,
  timeZone,
  validationError,
  createError,
  saving,
  onChange,
  onClose,
  onSubmit,
}) => (
  <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
    <DialogTitle>Out of Office</DialogTitle>
    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
      <FormControlLabel
        control={
          <Switch
            checked={draft.allDay}
            onChange={(event) => onChange({ ...EMPTY_DRAFT, allDay: event.target.checked })}
          />
        }
        label="All day"
      />
      <OutOfOfficeBoundaryFields
        allDay={draft.allDay}
        dateLabel="Start date"
        timeLabel="Start time"
        date={draft.startDate}
        time={draft.startTime}
        onDateChange={(startDate) => onChange({ ...draft, startDate })}
        onTimeChange={(startTime) => onChange({ ...draft, startTime })}
      />
      <OutOfOfficeBoundaryFields
        allDay={draft.allDay}
        dateLabel="End date"
        timeLabel="End time"
        date={draft.endDate}
        time={draft.endTime}
        onDateChange={(endDate) => onChange({ ...draft, endDate })}
        onTimeChange={(endTime) => onChange({ ...draft, endTime })}
      />
      <Typography sx={{ color: '#777', fontSize: 12 }}>Timezone: {timeZone}</Typography>
      {validationError ? <Alert severity="warning">{validationError}</Alert> : null}
      {createError ? (
        <Alert severity="error">We couldn’t save out of office. Please try again.</Alert>
      ) : null}
    </DialogContent>
    <DialogActions>
      <Button type="button" variant="text" disabled={saving} onClick={onClose}>
        Cancel
      </Button>
      <Button type="button" variant="contained" disabled={saving} onClick={onSubmit}>
        Confirm Out of Office
      </Button>
    </DialogActions>
  </Dialog>
);

interface OutOfOfficeBoundaryFieldsProps {
  allDay: boolean;
  dateLabel: string;
  timeLabel: string;
  date: string;
  time: string;
  onDateChange: (value: string) => void;
  onTimeChange: (value: string) => void;
}

const OutOfOfficeBoundaryFields: React.FC<OutOfOfficeBoundaryFieldsProps> = ({
  allDay,
  dateLabel,
  timeLabel,
  date,
  time,
  onDateChange,
  onTimeChange,
}) => (
  <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
    <TextField
      label={dateLabel}
      type="date"
      value={date}
      required
      InputLabelProps={{ shrink: true }}
      sx={{ flex: '1 1 180px' }}
      onChange={(event) => onDateChange(event.target.value)}
    />
    {allDay ? null : (
      <TimeSelect
        label={timeLabel}
        value={time}
        variant="outlined"
        native
        sx={{ flex: '0 0 120px' }}
        onChange={onTimeChange}
      />
    )}
  </Box>
);

function toLocalDateTime(date: string, time: string): string {
  return `${date}T${time}`;
}

function validateDraft(draft: OutOfOfficeDraft): string {
  if (draft.allDay) {
    if (!draft.startDate || !draft.endDate) {
      return 'Choose a start and end.';
    }
    if (draft.startDate > draft.endDate) {
      return 'The start date must be on or before the end date.';
    }
    return '';
  }
  if (!draft.startDate || !draft.endDate || !draft.startTime || !draft.endTime) {
    return 'Choose a start and end.';
  }
  const start = toLocalDateTime(draft.startDate, draft.startTime);
  const end = toLocalDateTime(draft.endDate, draft.endTime);
  if (start >= end) {
    return 'The start time must be before the end time.';
  }
  return '';
}

function toCreateRequest(draft: OutOfOfficeDraft, timeZone: string): CreateOutOfOfficeRequest {
  return draft.allDay
    ? { all_day: true, start_date: draft.startDate, end_date: draft.endDate, time_zone: timeZone }
    : {
        all_day: false,
        start_local: toLocalDateTime(draft.startDate, draft.startTime),
        end_local: toLocalDateTime(draft.endDate, draft.endTime),
        time_zone: timeZone,
      };
}

function formatBlockRange(block: OutOfOfficeBlock): string {
  const options: Intl.DateTimeFormatOptions = block.all_day
    ? { dateStyle: 'medium', timeZone: block.time_zone }
    : { dateStyle: 'medium', timeStyle: 'short', timeZone: block.time_zone };
  const formatter = new Intl.DateTimeFormat(undefined, options);
  const end = new Date(block.end_at);
  const displayedEnd = block.all_day ? new Date(end.getTime() - 1) : end;
  return `${formatter.format(new Date(block.start_at))} – ${formatter.format(displayedEnd)}`;
}

// MEN-5421: the appointment's zone is usually the member's, not the coach's,
// so name it rather than letting the coach read it as their own clock.
function formatAppointmentStart(appointment: BrowserAppointment): string {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    timeZone: appointment.current_schedule.timezone,
    timeZoneName: 'short',
    year: 'numeric',
  }).format(new Date(appointment.current_schedule.start_at));
}

export default OutOfOfficeCard;
